import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { PartyPopper, Plus, Pencil, Trash2, Users } from "lucide-react";

const empty = {
  client_name: "", phone: "", party_date: "", party_time: "19:00",
  pax: 10, per_plate_rate: 500, notes: "",
};

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

const STATUS = {
  booked:    { bg: "bg-amber-100 text-amber-800",     label: "Booked" },
  completed: { bg: "bg-emerald-100 text-emerald-800", label: "Completed" },
  cancelled: { bg: "bg-red-100 text-red-800",         label: "Cancelled" },
};

export default function PartyBookingsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === "admin";
  const isManager = Boolean(user?.manager_name);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/party-bookings", { params });
      setRows(data);
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    const today = new Date().toISOString().slice(0, 10);
    setForm({ ...empty, party_date: today });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      client_name: r.client_name, phone: r.phone || "",
      party_date: r.party_date, party_time: r.party_time,
      pax: r.pax, per_plate_rate: r.per_plate_rate, notes: r.notes || "",
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = {
        ...form, pax: parseInt(form.pax, 10),
        per_plate_rate: parseFloat(form.per_plate_rate),
      };
      if (editing) { await api.put(`/party-bookings/${editing.id}`, body); toast.success("Booking updated"); }
      else { await api.post("/party-bookings", body); toast.success("Party booked"); }
      setOpen(false); await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const setStatus = async (r, status) => {
    try { await api.put(`/party-bookings/${r.id}`, { status }); toast.success(`Marked ${status}`); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed"); }
  };

  const doDelete = async (r) => {
    if (!window.confirm(`Delete booking for "${r.client_name}"?`)) return;
    try { await api.delete(`/party-bookings/${r.id}`); toast.success("Removed"); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed"); }
  };

  const preview = (parseFloat(form.pax) || 0) * (parseFloat(form.per_plate_rate) || 0);
  const totalRevenue = rows.filter(r => r.status !== "cancelled").reduce((s, r) => s + Number(r.total || 0), 0);

  return (
    <div data-testid="parties-page">
      <div className="fade-up flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs tracking-[0.3em] uppercase text-muted-foreground">Events</p>
          <h1 className="font-serif text-3xl sm:text-5xl tracking-tight mt-2 flex items-center gap-3">
            Party Bookings <PartyPopper className="w-8 h-8 text-primary" aria-hidden="true" />
          </h1>
          <p className="mt-3 text-muted-foreground">Every party booked by any manager, in one place.</p>
        </div>
        {(isManager || isOwner) && (
          <Button onClick={openNew} data-testid="new-party-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
            <Plus className="w-4 h-4 mr-2" aria-hidden="true" /> New booking
          </Button>
        )}
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="bg-primary text-primary-foreground p-6 fade-up" data-testid="parties-stat-total">
          <div className="text-xs uppercase tracking-widest opacity-70">Booked revenue (excl. cancelled)</div>
          <div className="mt-3 font-serif text-3xl sm:text-4xl">{fmt(totalRevenue)}</div>
          <div className="mt-1 text-xs opacity-70">{rows.filter(r => r.status !== "cancelled").length} parties</div>
        </div>
        <div className="bg-white border border-border p-6 fade-up">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Upcoming</div>
          <div className="mt-3 font-serif text-3xl sm:text-4xl">
            {rows.filter(r => r.status === "booked" && r.party_date >= new Date().toISOString().slice(0,10)).length}
          </div>
        </div>
        <div className="bg-white border border-border p-6 fade-up">
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Completed</div>
          <div className="mt-3 font-serif text-3xl sm:text-4xl">{rows.filter(r => r.status === "completed").length}</div>
        </div>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <Label className="text-xs uppercase tracking-widest text-muted-foreground">Status</Label>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-52" data-testid="parties-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="booked">Booked</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4">
        {loading ? (
          <div className="bg-white border border-border p-10 text-center text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-border p-14 text-center">
            <p className="font-serif text-2xl">No bookings yet.</p>
            <p className="text-muted-foreground mt-2">Tap "New booking" to record your first party.</p>
          </div>
        ) : rows.map((r) => {
          const s = STATUS[r.status] || STATUS.booked;
          return (
            <div key={r.id} className="bg-white border border-border p-6 fade-up" data-testid={`party-row-${r.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-[240px]">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-serif text-2xl tracking-tight">{r.client_name}</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs uppercase tracking-widest ${s.bg}`}>{s.label}</span>
                  </div>
                  <div className="mt-2 text-sm text-muted-foreground flex items-center gap-3 flex-wrap">
                    <span>{r.party_date} · {r.party_time}</span>
                    <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" aria-hidden="true" />{r.pax} pax</span>
                    <span>· {fmt(r.per_plate_rate)}/plate</span>
                    <span>· by <strong>{r.manager}</strong></span>
                    {r.phone && <span>· {r.phone}</span>}
                  </div>
                  {r.notes && <p className="mt-2 text-sm">{r.notes}</p>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <div className="font-serif text-3xl text-primary">{fmt(r.total)}</div>
                  <div className="flex gap-2">
                    {r.status === "booked" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setStatus(r, "cancelled")} data-testid={`cancel-party-${r.id}`}>Cancel</Button>
                        <Button size="sm" onClick={() => setStatus(r, "completed")} data-testid={`complete-party-${r.id}`} className="bg-emerald-600 hover:bg-emerald-700 text-white">Mark done</Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-party-${r.id}`}><Pencil className="w-4 h-4" aria-hidden="true" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => doDelete(r)} data-testid={`delete-party-${r.id}`}><Trash2 className="w-4 h-4 text-destructive" aria-hidden="true" /></Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="party-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight">{editing ? "Edit booking" : "New party booking"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cn">Client name *</Label>
                <Input id="cn" data-testid="party-client" required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} placeholder="e.g. Sharma Family" />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input id="phone" data-testid="party-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="9999999999" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pd">Party date *</Label>
                <Input id="pd" data-testid="party-date" type="date" required value={form.party_date} onChange={(e) => setForm({ ...form, party_date: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="pt">Party time *</Label>
                <Input id="pt" data-testid="party-time" type="time" required value={form.party_time} onChange={(e) => setForm({ ...form, party_time: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pax">Pax (guests) *</Label>
                <Input id="pax" data-testid="party-pax" type="number" min="1" required value={form.pax} onChange={(e) => setForm({ ...form, pax: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="rate">Per plate rate (₹) *</Label>
                <Input id="rate" data-testid="party-rate" type="number" min="0" step="0.01" required value={form.per_plate_rate} onChange={(e) => setForm({ ...form, per_plate_rate: e.target.value })} />
              </div>
            </div>
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" data-testid="party-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Menu preferences, allergies, decor…" />
            </div>
            <div className="bg-secondary p-4 border border-border">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
              <div className="font-serif text-3xl text-primary" data-testid="party-total-preview">{fmt(preview)}</div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="save-party-btn" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                {saving ? "Saving…" : editing ? "Save" : "Book party"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
